import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchAiProviderStatus } from 'services/aiStatusService'
import { AI_STATUS_REFRESH_EVENT } from 'utils/aiStatus'

export const AI_STATUS_POLL_MS = 60000
export const AI_STATUS_DEGRADED_POLL_MS = 20000

export const nextBannerState = (previous, serverStatus) => {
  if (serverStatus === 'degraded') return 'degraded'
  if (previous === 'degraded' || previous === 'restored') return 'restored'
  return 'hidden'
}

const useAiProviderStatus = enabled => {
  const [banner, setBanner] = useState('hidden')
  const bannerRef = useRef('hidden')
  const timerRef = useRef(null)
  const requestRef = useRef(0)

  const apply = useCallback(next => {
    bannerRef.current = next
    setBanner(next)
  }, [])

  const check = useCallback(async () => {
    const requestId = ++requestRef.current
    try {
      const response = await fetchAiProviderStatus()
      if (requestId !== requestRef.current) return
      const serverStatus = response?.data?.data?.status || 'ok'
      const next = nextBannerState(bannerRef.current, serverStatus)
      if (next !== bannerRef.current) apply(next)
    } catch (e) {
      return undefined
    }
  }, [apply])

  useEffect(() => {
    if (!enabled) {
      apply('hidden')
      return undefined
    }

    let cancelled = false
    const schedule = () => {
      clearTimeout(timerRef.current)
      const delay = bannerRef.current === 'degraded' ? AI_STATUS_DEGRADED_POLL_MS : AI_STATUS_POLL_MS
      timerRef.current = setTimeout(async () => {
        await check()
        if (!cancelled) schedule()
      }, delay)
    }

    const refreshNow = () => {
      check().then(() => { if (!cancelled) schedule() })
    }

    refreshNow()
    window.addEventListener(AI_STATUS_REFRESH_EVENT, refreshNow)
    window.addEventListener('focus', refreshNow)

    return () => {
      cancelled = true
      clearTimeout(timerRef.current)
      window.removeEventListener(AI_STATUS_REFRESH_EVENT, refreshNow)
      window.removeEventListener('focus', refreshNow)
    }
  }, [enabled, check, apply])

  const dismissRestored = useCallback(() => {
    if (bannerRef.current === 'restored') apply('hidden')
  }, [apply])

  return { banner, dismissRestored }
}

export default useAiProviderStatus
